import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState, type TouchEvent } from "react";

import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";

// Ported from the reference voice-message-player.tsx with the theme colors
// swapped for Mandoob tokens: custom play/pause, a decoded waveform (flat
// placeholder bars until — or if — decoding finishes), drag/tap seeking and a
// playback-speed toggle.

const BAR_COUNT = 42;
const PLAYBACK_SPEEDS = [1, 1.5, 2];
const SPEED_STORAGE_KEY = "mandoob:audio-speed";

// flat mid-height bars shown while the real waveform decodes (or when the
// audio codec can't be decoded for analysis — playback still works).
const PLACEHOLDER_BARS = Array.from({ length: BAR_COUNT }, () => 0.3);

function savedSpeed(): number {
  try {
    const speed = Number(localStorage.getItem(SPEED_STORAGE_KEY));
    return PLAYBACK_SPEEDS.includes(speed) ? speed : 1;
  } catch {
    return 1;
  }
}

function saveSpeed(speed: number): void {
  try {
    localStorage.setItem(SPEED_STORAGE_KEY, String(speed));
  } catch {
    // localStorage unavailable — the speed still applies for this session.
  }
}

function formatDuration(seconds: number): string {
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface Waveform {
  bars: number[];
  duration: number;
}

function barsFromBuffer(buffer: AudioBuffer): number[] {
  const data = buffer.getChannelData(0);
  const bucket = Math.max(1, Math.floor(data.length / BAR_COUNT));
  const bars: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    let peak = 0;
    const from = i * bucket;
    const to = Math.min(data.length, from + bucket);
    for (let j = from; j < to; j++) {
      const v = Math.abs(data[j]);
      if (v > peak) peak = v;
    }
    bars.push(peak);
  }
  const max = Math.max(...bars, 0.01);
  return bars.map((bar) => bar / max);
}

// decoded waveforms are cached per url so re-renders and thread remounts don't
// re-fetch and re-decode the same audio.
const waveformCache = new Map<string, Promise<Waveform | null>>();

function loadWaveform(url: string): Promise<Waveform | null> {
  let cached = waveformCache.get(url);
  if (!cached) {
    cached = (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const encoded = await res.arrayBuffer();
        const ctx = new OfflineAudioContext(1, 1, 48000);
        const buffer = await ctx.decodeAudioData(encoded);
        return { bars: barsFromBuffer(buffer), duration: buffer.duration };
      } catch {
        return null;
      }
    })();
    waveformCache.set(url, cached);
  }
  return cached;
}

export function VoiceMessagePlayer({ url }: { url: string }) {
  const { t, dir } = useLanguage();
  // the waveform runs against the UI direction: progress moves right-to-left
  // in LTR layouts and left-to-right in RTL ones.
  const waveDir = dir === "rtl" ? "ltr" : "rtl";
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const scrubbingRef = useRef(false);
  const [bars, setBars] = useState<number[] | null>(null);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(savedSpeed);

  useEffect(() => {
    let cancelled = false;
    loadWaveform(url).then((waveform) => {
      if (cancelled || !waveform) return;
      setBars(waveform.bars);
      // decoded duration is exact and covers formats where the media element
      // reports Infinity (e.g. streamed ogg/webm voice notes).
      if (waveform.duration > 0) setDuration(waveform.duration);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    if (!playing) return;
    const tick = () => {
      const audio = audioRef.current;
      if (audio && !scrubbingRef.current) setElapsed(audio.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [playing]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    // replay from the start when sitting at the end of the clip.
    if (duration > 0 && elapsed >= duration - 0.05) audio.currentTime = 0;
    audio.playbackRate = speed;
    audio.play().catch(() => undefined);
  };

  const cycleSpeed = () => {
    const next =
      PLAYBACK_SPEEDS[
        (PLAYBACK_SPEEDS.indexOf(speed) + 1) % PLAYBACK_SPEEDS.length
      ];
    setSpeed(next);
    saveSpeed(next);
    const audio = audioRef.current;
    if (audio) audio.playbackRate = next;
  };

  const seekToClientX = (clientX: number) => {
    const wave = waveRef.current;
    const audio = audioRef.current;
    if (!wave || !audio || duration <= 0) return;
    const rect = wave.getBoundingClientRect();
    let frac =
      rect.width > 0
        ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        : 0;
    if (waveDir === "rtl") frac = 1 - frac;
    audio.currentTime = frac * duration;
    setElapsed(frac * duration);
  };

  // horizontal drags on the waveform are seeks — keep them from bubbling into
  // any ancestor touch handlers.
  const stopTouch = (event: TouchEvent) => event.stopPropagation();

  const progress = duration > 0 ? Math.min(1, elapsed / duration) : 0;
  const displayBars = bars ?? PLACEHOLDER_BARS;
  const playedBars = progress * displayBars.length;

  return (
    <div className="tw:relative tw:mb-0.5 tw:flex tw:w-72 tw:max-w-full tw:items-center tw:gap-2.5 tw:px-1 tw:py-1.5">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(event) => {
          const mediaDuration = event.currentTarget.duration;
          if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
            setDuration((prev) => prev || mediaDuration);
          }
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          const audio = audioRef.current;
          if (audio) audio.currentTime = 0;
          setElapsed(0);
        }}
        className="tw:hidden"
      />
      <div
        ref={waveRef}
        dir={waveDir}
        role="slider"
        aria-label={t("Mandoob:Player.Seek")}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(elapsed)}
        tabIndex={0}
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          scrubbingRef.current = true;
          seekToClientX(event.clientX);
        }}
        onPointerMove={(event) => {
          if (scrubbingRef.current) seekToClientX(event.clientX);
        }}
        onPointerUp={() => {
          scrubbingRef.current = false;
        }}
        onPointerCancel={() => {
          scrubbingRef.current = false;
        }}
        onTouchStart={stopTouch}
        onTouchMove={stopTouch}
        onTouchEnd={stopTouch}
        className="tw:relative tw:flex tw:h-[26px] tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:touch-none tw:items-center tw:gap-[2px]"
      >
        {displayBars.map((amp, index) => (
          <span
            key={index}
            style={{ height: `${Math.max(10, Math.min(100, amp * 100))}%` }}
            className={cn(
              "tw:min-w-0 tw:flex-1 tw:rounded-full",
              index < playedBars
                ? "tw:bg-muted-foreground"
                : "tw:bg-foreground/20",
            )}
          />
        ))}
        <span
          aria-hidden
          style={{ insetInlineStart: `calc(${progress} * (100% - 12px))` }}
          className="tw:absolute tw:top-1/2 tw:size-3 tw:-translate-y-1/2 tw:rounded-full tw:bg-brand tw:shadow-sm"
        />
      </div>
      {/* out of flow so the waveform stays centered on the play icon; lands on
          the same line as the bubble's own timestamp row that follows, in the
          corner the timestamp doesn't occupy. The outer span positions with
          the APP direction (dir="ltr" here would make `start` physical-left in
          both languages); the inner one only keeps the digits LTR. */}
      <span className="tw:absolute tw:start-1 tw:top-full tw:text-[11px] tw:text-muted-foreground">
        <span dir="ltr" className="tw:tabular-nums">
          {formatDuration(elapsed > 0 ? elapsed : duration)}
        </span>
      </span>
      <button
        type="button"
        aria-label={
          playing ? t("Mandoob:Player.Pause") : t("Mandoob:Player.Play")
        }
        onClick={togglePlay}
        className="tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:text-muted-foreground"
      >
        {playing ? (
          <Pause className="tw:size-6 tw:fill-current tw:stroke-none" />
        ) : (
          <Play className="tw:size-6 tw:fill-current tw:stroke-none" />
        )}
      </button>
      <button
        type="button"
        dir="ltr"
        aria-label={t("Mandoob:Player.Speed")}
        onClick={cycleSpeed}
        className="tw:h-6 tw:w-10 tw:shrink-0 tw:rounded-full tw:bg-foreground/[0.08] tw:text-[11px] tw:font-semibold tw:leading-none tw:tabular-nums tw:text-muted-foreground tw:transition-colors tw:hover:bg-foreground/[0.14]"
      >
        {speed}×
      </button>
    </div>
  );
}
